// The accepted-move hook (#6772, epic #6552): what every move this page sent and the server
// ACCEPTED leaves behind. `lib/move-page.ts` calls {@link AcceptedMoves.accept} on every 201
// (drag, Move to, the Move work panel, Undo, each Put-back part), before its re-read: the note,
// the Moving tag and the transfer motion start on the 201, never on a timer, but everything they
// then SAY about where a task is (landed, Undo live, Put back's list and its "all back") is
// derived from fresh task reads, never from the 201.
//
// WHERE A MOVE'S STATE COMES FROM. For each task of a batch this page accepted (the 201's
// `batch.items[].task_id`, at most 8) the page follows that task with `useTaskDetail` (live on
// the `task` entity: a move item's state change bumps the task's `updated_at`), one read per id
// in a `mapArray`, as `createSendWorkPage` follows its sent ids. Not `GET
// /v1/tasks?filter=runner_id:eq:<id>`: a moving task is `pending` with `runner_id` null, so it
// is in neither machine's read. It follows the newest batch, every task this page moved (Put
// back's list: home is a reading another tab can change), and any task still tagged Moving or
// Moved, or still resting (its ring). A job moved only by another tab shows no tag and no ring:
// its preview's `cooling_down` still disables it.
//
// PAGE MEMORY ONLY: the newest batch (the note and its Undo), and for each task moved since the
// page loaded, its home (the machine before its FIRST move here; neither an Undo nor a Put-back
// part overwrites it). Where a task IS is never stored: it is derived from its newest fresh
// read on every render (`movePlace`), which is what Put everything back sends from.

import { MOVE_REST_MS } from '@developerz/domain';
import {
  createContext,
  createEffect,
  createMemo,
  createSignal,
  mapArray,
  onCleanup,
  untrack,
  useContext,
} from 'solid-js';
import { t, tPlural } from '../i18n';
import { jobState } from './machine-jobs';
import type { MoveItem } from './move-calls';
import {
  type MoveBatch,
  type MoveNoteView,
  moveNoteView,
  type TaskMove,
  UNDO_LIVE_MS,
  undoView,
} from './move-note';
import type { AcceptedMove, MoveOutcome, MovePage, MoveRequest } from './move-page';
import {
  type AcceptedMoveRef,
  judgeMoveRead,
  type MoveJudgement,
  type MovePlace,
  type MoveRead,
  movePlace,
  moveSettled,
} from './move-read';
import { type PutBackPart, putBackParts, runPutBack } from './put-back';
import { orderStamp } from './read-order';
import { useTaskMoveRead } from './tasks';
import { TRANSFER } from './transfer-motion';

/** A task this page follows: the move it accepted (`AcceptedMoveRef`) and when (`Date.now`). */
type Followed = AcceptedMoveRef & {
  readonly id: string;
  /** When the 201 landed, `Date.now` ms: the Moving tag holds at least 1,100 ms from it. */
  readonly acceptedAt: number;
};

/** The row tag a followed task shows: Moving while in flight, then Moved. */
export type MoveTag = 'moving' | 'moved';

export type AcceptedMovesDeps = {
  readonly page: () => MovePage;
  readonly token: string | null;
  /**
   * The transfer motion for one accepted move (`./transfer-capture.ts` in the browser). It calls
   * `done` once the motion ends, so its handle is released then, not kept until unmount.
   */
  readonly motion?: (a: AcceptedMove, done: () => void) => { readonly dispose: () => void } | null;
  /**
   * A task's live move read, stamped with when its request started (`useTaskMoveRead`) unless a
   * test injects one; `refresh` re-reads it now.
   */
  readonly follow?: (
    id: () => string,
    token: string | null,
  ) => { readonly data?: MoveRead | undefined; readonly refresh: () => void };
};

/** The followed reads, by id: each task's newest stamped read, and a way to ask for a new one. */
function followReads(deps: AcceptedMovesDeps, followed: () => readonly Followed[]) {
  const follow = deps.follow ?? ((id, token) => useTaskMoveRead(id, token));
  const ids = createMemo(() => followed().map((f) => f.id), undefined, {
    equals: (a, b) => a.length === b.length && a.every((x, i) => x === b[i]),
  });
  const reads = createMemo(
    mapArray(ids, (id) => {
      const read = follow(() => id, deps.token);
      return { id, read: (): MoveRead | undefined => read.data, refresh: read.refresh };
    }),
  );
  const of = (taskId: string) => reads().find((r) => r.id === taskId);
  return {
    readOf: (taskId: string): MoveRead | undefined => of(taskId)?.read(),
    refresh: (taskId: string): void => of(taskId)?.refresh(),
  };
}

/** A request that sends tasks one by one (Undo, a Put-back part). */
function taskRequest(
  part: { readonly fromId: string; readonly toId: string; readonly taskIds: readonly string[] },
  extra: Pick<MoveRequest, 'key' | 'what' | 'zone'> & Partial<MoveRequest>,
): MoveRequest {
  const items: MoveItem[] = part.taskIds.map((id) => ({ kind: 'task', id }));
  return {
    sourceId: part.fromId,
    targetId: part.toId,
    items,
    many: part.taskIds.length > 1,
    ...extra,
  };
}

/** The followed tasks, their reads, and the tags and rings those reads show. */
function createFollow(
  deps: AcceptedMovesDeps,
  page: () => MovePage,
  /** Tasks to keep following whatever their reads show (the note's batch, Put back's list). */
  keep: () => readonly string[],
) {
  const [followed, setFollowed] = createSignal<readonly Followed[]>([]);
  const [landedAt, setLandedAt] = createSignal<ReadonlyMap<string, number>>(new Map());
  const reads = followReads(deps, followed);
  const entryOf = (id: string): Followed | undefined => followed().find((f) => f.id === id);
  /** The task's newest move this page accepted, judged by fresh reads only (`judgeMoveRead`). */
  const judge = (id: string): MoveJudgement => {
    const f = entryOf(id);
    return f === undefined ? { kind: 'pending' } : judgeMoveRead(reads.readOf(id), f);
  };
  /** The judged read's move: undefined while pending (Undo reads it as "not landed yet"). */
  const moveOf = (id: string): TaskMove | undefined => {
    const j = judge(id);
    return j.kind === 'pending' ? undefined : j.move;
  };
  // A followed read answered: the clock is read again, so its `rest_until` counts from now and
  // not from the page clock's last tick, up to a second old.
  createEffect(() => {
    for (const f of followed()) reads.readOf(f.id);
    untrack(() => page().readClock());
  });
  // The first fresh read that shows each followed task landed: the Moved tag counts from it.
  createEffect(() => {
    const now = Date.now();
    const seen = untrack(landedAt);
    const fresh = followed().filter((f) => {
      const j = judge(f.id);
      return !seen.has(f.id) && j.kind === 'ours' && j.move.state === 'landed';
    });
    if (fresh.length === 0) return;
    const next = new Map(seen);
    for (const f of fresh) next.set(f.id, now);
    setLandedAt(next);
  });
  const tagOf = (taskId: string): MoveTag | null =>
    moveTag(judge(taskId), entryOf(taskId)?.acceptedAt ?? null, landedAt().get(taskId), page());
  /** A followed task's rest while it lasts: the ring drains from start to end. */
  const restOf = (taskId: string): { readonly startMs: number; readonly endMs: number } | null => {
    const j = judge(taskId);
    if (j.kind !== 'ours' || j.move.state !== 'landed') return null;
    const end = Date.parse(j.move.rest_until);
    if (Number.isNaN(end) || end <= page().clock()) return null;
    page().tickUntil(end);
    return { startMs: end - MOVE_REST_MS, endMs: end };
  };
  // Stop following what no longer shows anything. A task stays followed, whatever its tag and
  // ring, until a fresh read has SETTLED its move (landed, failed, or superseded by a later batch), and
  // for as long as `keep` names it.
  createEffect(() => {
    page().clock();
    const kept = new Set(keep());
    const next = followed().filter(
      (f) =>
        kept.has(f.id) ||
        untrack(() => !moveSettled(judge(f.id)) || tagOf(f.id) !== null || restOf(f.id) !== null),
    );
    if (next.length === untrack(followed).length) return;
    setFollowed(next);
    // A Moved tag's start is kept only while its task is followed.
    const ids = new Set(next.map((f) => f.id));
    setLandedAt((m) => new Map([...m].filter(([id]) => ids.has(id))));
  });
  return {
    moveOf,
    /**
     * A followed task's newest stamped read, UNJUDGED (#6773): the transfer stack judges it with
     * each card's own accepted ref (`judgeMoveRead`), never with the task's newest (`judge`).
     */
    readOf: reads.readOf,
    /**
     * Where a followed task is, DERIVED from its newest read (`movePlace`); null when it is not
     * followed, so nothing is known of it.
     */
    placeOf: (id: string): MovePlace | null => {
      const f = entryOf(id);
      return f === undefined ? null : movePlace(reads.readOf(id), f.order);
    },
    /** A fresh read shows a later batch of the task: this page's move is no longer its newest. */
    superseded: (id: string): boolean => judge(id).kind === 'superseded',
    tagOf,
    restOf,
    /** Follow these tasks from now in `batchId`, accepted at `order` (a 201), and re-read them. */
    follow(ids: readonly string[], now: number, ref: AcceptedMoveRef): void {
      setFollowed((f) => [
        ...f.filter((x) => !ids.includes(x.id)),
        ...ids.map((id) => ({ id, acceptedAt: now, ...ref })),
      ]);
      setLandedAt((m) => {
        const next = new Map(m);
        for (const id of ids) next.delete(id);
        return next;
      });
      // A read already held for a task this page moves again predates this 201: ask for a fresh one.
      for (const id of ids) reads.refresh(id);
    },
  };
}

/**
 * The tag a task shows at the page clock: Moving while its move is pending or in transit (at
 * least 1,100 ms from the 201), then Moved for 2,300 ms from the first fresh landed read; none
 * once a fresh read shows it failed or superseded. The clock is kept ticking to the change.
 */
function moveTag(
  j: MoveJudgement,
  acceptedAt: number | null,
  landedAt: number | undefined,
  page: MovePage,
): MoveTag | null {
  if (acceptedAt === null || j.kind === 'superseded') return null;
  const now = page.clock();
  const state = j.kind === 'ours' ? j.move.state : undefined;
  const minEnd = acceptedAt + TRANSFER.movingMinMs;
  if (state !== 'landed' && state !== 'failed') return 'moving';
  if (now < minEnd) {
    page.tickUntil(minEnd);
    return 'moving';
  }
  if (state === 'failed') return null;
  const movedEnd = Math.max(landedAt ?? now, minEnd) + TRANSFER.movedMs;
  page.tickUntil(movedEnd);
  return now < movedEnd ? 'moved' : null;
}

type Follow = ReturnType<typeof createFollow>;

/** The newest batch, its note and its Undo. */
function createNewest(page: () => MovePage, follow: () => Follow) {
  const [newest, setNewest] = createSignal<MoveBatch | null>(null);
  // Every answer below is KEYED BY THE BATCH it was for (#6772 review round 3): an answer for
  // a batch that is no longer the newest never reaches the one that is.
  /** When a batch was found never undoable (a failed item, a later batch, refused for good). */
  const [failedAt, setFailedAt] = createSignal<{ readonly id: string; readonly at: number } | null>(
    null,
  );
  /** The batch whose Undo is in flight. */
  const [undoing, setUndoing] = createSignal<string | null>(null);
  // The batch whose Undo the server refused for good (`undo_unavailable`): offered no more.
  const [unavailable, setUnavailable] = createSignal<string | null>(null);
  /** Mark `id` never undoable from now; its note leaves 7 s later. */
  const markFailed = (id: string): void => {
    const at = Date.now();
    setFailedAt({ id, at });
    page().tickUntil(at + UNDO_LIVE_MS);
  };
  const failedFor = (id: string): number | null => {
    const f = failedAt();
    return f !== null && f.id === id ? f.at : null;
  };
  let disposed = false;
  onCleanup(() => {
    disposed = true;
  });
  const moveOf = (id: string) => follow().moveOf(id);
  const nameOf = (id: string): string => page().boxOf(id)?.display_name ?? id.slice(0, 8);
  const starting = (taskId: string): boolean => {
    const b = newest();
    if (b === null) return false;
    const job = page()
      .boxOf(b.toId)
      ?.jobs?.find((j) => j.id === taskId);
    return job !== undefined && jobState(job) === 'starting';
  };
  /** The newest batch's note and Undo, as the page clock reads them. */
  const note = createMemo((): MoveNoteView | null => {
    const b = newest();
    if (b === null) return null;
    // A later batch of a task superseded this one: Undo can no longer reach it, and while that
    // batch is in transit or landed the note would name a machine the task is leaving or left.
    // Retired at once (#6772 review round 6); a failed item of THIS batch keeps the timed note.
    if (b.taskIds.some((id) => follow().superseded(id))) return null;
    const v = moveNoteView(
      {
        batch: b,
        newestId: b.id,
        rendered: page().rendered(),
        moveOf,
        starting,
        failedAt: failedFor(b.id),
        now: page().clock(),
        nameOf,
      },
      t,
    );
    if (v !== null && unavailable() === b.id) return { ...v, undo: { kind: 'none' } };
    if (v !== null && (v.undo.kind === 'resting' || v.undo.kind === 'live')) {
      page().tickUntil(v.undo.untilMs);
    }
    return v;
  });
  // A read that shows the batch can never be undone (an item failed, or a later batch): the note
  // stays 7 s from that read, then goes.
  createEffect(() => {
    const b = newest();
    if (b === null || failedFor(b.id) !== null) return;
    if (undoView(b, moveOf, () => false, Date.now()).kind !== 'none') return;
    markFailed(b.id);
  });
  const undo = (): void => {
    const b = newest();
    if (b === null || note()?.undo.kind !== 'live' || untrack(undoing) !== null) return;
    const p = page();
    if (p.locked() > 0) {
      p.announce(t('fleet.machines.ctl_model_locked'));
      return;
    }
    const blocked = p.blockedWhy(b.toId);
    if (blocked !== null) {
      p.announce(blocked);
      return;
    }
    setUndoing(b.id);
    const req = taskRequest(
      { fromId: b.toId, toId: b.fromId, taskIds: b.taskIds },
      {
        key: `undo:${b.id}`,
        what: b.what,
        zone: b.zone,
        undoOf: b.id,
        // Said on the 201, so in progress: the Undo can still fail (review round 10).
        announce: t('fleet.move.undo_moving', { what: b.what, name: nameOf(b.fromId) }),
      },
    );
    void p
      .moveMany(req)
      .then((outcome) => {
        // Permanent: the batch can never be undone; the note leaves 7 s from now. Any other
        // refusal (still starting, a budget, a machine offline) can clear, so Undo stays.
        if (disposed || outcome.kind !== 'refused') return;
        if (outcome.refusal.reason !== 'undo_unavailable') return;
        // For THIS batch only: if another move was accepted meanwhile, its note is untouched.
        setUnavailable(b.id);
        markFailed(b.id);
      })
      .finally(() => {
        if (!disposed) setUndoing((cur) => (cur === b.id ? null : cur));
      });
  };
  return {
    newest,
    note,
    undo,
    /** An Undo is in flight for the newest batch. */
    undoing: (): boolean => {
      const b = newest();
      return b !== null && undoing() === b.id;
    },
    set(b: MoveBatch): void {
      setNewest(b);
    },
  };
}

/** Where a task this page moved stands against its home, as its newest read has it. */
type Standing =
  | { readonly kind: 'pending' }
  | { readonly kind: 'home' }
  | { readonly kind: 'away'; readonly runnerId: string }
  /** A fresh read shows it no longer holds a place on any machine: it leaves the list. */
  | { readonly kind: 'gone' }
  /**
   * The read names no machine (it was removed), or the task is not followed: nothing to send it
   * from, and not known to be home. Listed as waiting, like `pending`, and never "all back".
   */
  | { readonly kind: 'unknown' };

/**
 * Put everything back: the page-memory homes, and the run over them.
 *
 * NOTHING HERE IS A LEDGER OF EVENTS (#6772 review round 8). Where a task is, is DERIVED on every
 * read from its newest fresh task read (`movePlace`): landed, the batch's target; failed, its
 * source; in transit, pending. A 201 (a move, an Undo, a Put-back part) only re-orders the read
 * (`follow`), so an older read no longer counts; another tab's later batch replaces the answer
 * the moment a read shows it. The two things held are each superseded by a read: `homes` (the
 * machine before this page's FIRST move of a task, a fact the first 201's request names) and
 * `awaiting` (the tasks a finished run sent, until fresh reads show each one home or not).
 */
function createPutBack(page: () => MovePage, follow: () => Follow) {
  const [homes, setHomes] = createSignal<ReadonlyMap<string, string>>(new Map());
  // The tasks the last run's accepted parts sent: "all back" is said only once fresh reads show
  // every one of them home. A new run, or those reads, clear it.
  const [awaiting, setAwaiting] = createSignal<readonly string[] | null>(null);
  const [putting, setPutting] = createSignal(false);
  let disposed = false;
  onCleanup(() => {
    disposed = true;
  });
  const standing = (id: string, home: string): Standing => {
    const p = follow().placeOf(id);
    if (p === null) return { kind: 'unknown' };
    if (p.kind === 'pending') return { kind: 'pending' };
    if (p.kind === 'gone') return { kind: 'gone' };
    if (p.runnerId === null) return { kind: 'unknown' };
    return p.runnerId === home ? { kind: 'home' } : { kind: 'away', runnerId: p.runnerId };
  };
  const standings = createMemo(() =>
    [...homes()].map(([id, home]) => ({ id, home, s: standing(id, home) })),
  );
  /** Where each task off its home is now: what a run sends from. */
  const where = (): ReadonlyMap<string, string> =>
    new Map(standings().flatMap((x) => (x.s.kind === 'away' ? [[x.id, x.s.runnerId]] : [])));
  const pending = (): boolean => standings().some((x) => x.s.kind === 'pending');
  /** Not known to be anywhere: listed, waiting for a read that names its machine. */
  const unknown = (): boolean => standings().some((x) => x.s.kind === 'unknown');
  // A task a fresh read shows finished or dropped can never be put back: it leaves `homes`, and
  // with it the follow (`keep`). This is what bounds both on a long-lived page: they hold only
  // tasks this viewer moved that still hold a place on a machine (#6772 review round 11).
  createEffect(() => {
    const gone = standings()
      .filter((x) => x.s.kind === 'gone')
      .map((x) => x.id);
    if (gone.length === 0) return;
    setHomes((h) => new Map([...h].filter(([id]) => !gone.includes(id))));
  });
  // "All back" is said only once fresh reads show EVERY task this page moved home: each one
  // the run sent, and every other (another tab may have moved one away meanwhile, or a read may
  // name no machine). In transit holds the verdict; anything not home (a restore that failed, a
  // move elsewhere, an unknown machine) ends the wait silently, and it stays listed.
  createEffect(() => {
    const ids = awaiting();
    if (ids === null) return;
    const all = standings();
    // In transit, or no read since its put-back's 201 (`movePlace` is pending until one).
    if (all.some((x) => x.s.kind === 'pending')) return;
    setAwaiting(null);
    const back = all.every((x) => x.s.kind === 'home' || x.s.kind === 'gone');
    if (back) page().announce(t('fleet.move.put_back_done'));
  });
  /** One part, held by the page's own pre-send check before it is sent. */
  const sendPart = (part: PutBackPart): Promise<MoveOutcome> => {
    const p = page();
    if (p.blockedWhy(part.fromId) !== null) return Promise.resolve({ kind: 'held' });
    const n = part.taskIds.length;
    return p.moveMany(
      taskRequest(part, {
        key: `putback:${part.fromId}>${part.toId}`,
        what: tPlural('fleet.move.work_jobs', n, { count: n }),
        zone: 'now',
        putBack: true,
      }),
    );
  };
  const run = async (): Promise<void> => {
    if (untrack(putting)) return;
    const p = page();
    // In transit: wait for it. Unknown with nothing else to send: it waits for a read too.
    if (untrack(pending) || (untrack(where).size === 0 && untrack(unknown))) {
      p.announce(t('fleet.move.put_back_wait'));
      return;
    }
    if (p.locked() > 0) {
      p.announce(t('fleet.machines.ctl_model_locked'));
      return;
    }
    setPutting(true);
    setAwaiting(null);
    const sent: string[] = [];
    const result = await runPutBack(putBackParts(untrack(homes), untrack(where)), sendPart, {
      // A part's 201 does not make its tasks home: fresh reads of them do (`awaiting`).
      landed: (part) => sent.push(...part.taskIds),
      // `item_gone` is the server's word that the job left for good: not this page's to restore.
      dropped: (id) =>
        setHomes((h) => {
          const next = new Map(h);
          next.delete(id);
          return next;
        }),
    });
    if (disposed) return;
    setPutting(false);
    // Every part accepted: wait for fresh reads to show each task home before saying so.
    if (result.kind === 'done') {
      if (sent.length > 0) setAwaiting(sent);
      return;
    }
    // A part refused for now was announced by its send and stays listed: not "all back".
    if (result.kind === 'kept') return;
    // A held part says why; a refusal, a wait or a failure was already announced by its send.
    if (result.outcome.kind === 'held') {
      p.announce(p.blockedWhy(result.part.fromId) ?? t('fleet.machines.ctl_model_locked'));
    }
  };
  return {
    putting,
    run,
    /**
     * Offered while any task this viewer moved is not known to be home: in transit, a fresh read
     * showing it off home, or a read that names no machine (listed as waiting).
     */
    movedAway: (): boolean => standings().some((x) => x.s.kind !== 'home' && x.s.kind !== 'gone'),
    /**
     * Every task this page moved stays followed for the page's life: home is a reading, not a
     * resting state, and another tab may move a task that is home now. (One read per task this
     * viewer moved; `item_gone` is the one way off the list.)
     */
    keep: (): readonly string[] => [...homes().keys()],
    /** A 201 accepted `ids` off `fromId`: their home, if this page never moved them before. */
    moved(ids: readonly string[], fromId: string): void {
      setHomes((h) => {
        const next = new Map(h);
        for (const id of ids) if (!next.has(id)) next.set(id, fromId);
        return next;
      });
    },
  };
}

export function createAcceptedMoves(deps: AcceptedMovesDeps) {
  const page = (): MovePage => deps.page();
  let follow: Follow | undefined;
  const followed = (): Follow => {
    if (follow === undefined) throw new Error('accepted moves read before they were made');
    return follow;
  };
  const newest = createNewest(page, followed);
  const back = createPutBack(page, followed);
  // Tasks an open transfer card follows (#6773), one entry per card: released when the card
  // settles or leaves, so it holds at most the stack's 24 cards' tasks.
  const [held, setHeld] = createSignal<ReadonlyMap<number, readonly string[]>>(new Map());
  let holds = 0;
  follow = createFollow(deps, page, () => [
    ...(newest.newest()?.taskIds ?? []),
    ...back.keep(),
    ...[...held().values()].flat(),
  ]);
  const motions = new Set<{ readonly dispose: () => void }>();
  let disposed = false;
  onCleanup(() => {
    disposed = true;
    for (const m of motions) m.dispose();
    motions.clear();
  });
  /** Follow the move, and hand back the exact ref it is judged by (the transfer card's). */
  const accept = (a: AcceptedMove): AcceptedMoveRef | null => {
    if (disposed) return null;
    const req = a.request;
    const now = Date.now();
    const ids = (a.accepted.batch.items ?? []).map((i) => i.task_id);
    newest.set({
      id: a.accepted.batch.id,
      fromId: req.sourceId,
      toId: req.targetId,
      taskIds: ids,
      what: req.what,
      zone: req.zone,
      undo: req.undoOf !== undefined || req.putBack === true,
    });
    back.moved(ids, req.sourceId);
    const ref: AcceptedMoveRef = { batchId: a.accepted.batch.id, order: orderStamp() };
    followed().follow(ids, now, ref);
    page().tickUntil(now + TRANSFER.movingMinMs);
    // Held only while it plays (#6772 review round 11): a long-lived page accepts many moves.
    let played: { readonly dispose: () => void } | null = null;
    let ended = false;
    const release = (): void => {
      ended = true;
      if (played !== null) motions.delete(played);
    };
    played = deps.motion?.(a, release) ?? null;
    if (played !== null && !ended) motions.add(played);
    return ref;
  };
  return {
    accept,
    note: newest.note,
    undo: newest.undo,
    undoing: newest.undoing,
    tagOf: (id: string) => followed().tagOf(id),
    restOf: (id: string) => followed().restOf(id),
    movedAway: back.movedAway,
    putBack: back.run,
    putting: back.putting,
    /** A followed task's newest stamped read, unjudged (#6773). */
    readOf: (id: string): MoveRead | undefined => followed().readOf(id),
    /** The newest batch's id: the one whose note and Undo show (`note()`). */
    newestBatchId: (): string | null => newest.newest()?.id ?? null,
    /**
     * Keep following `ids` whatever their reads show (an open transfer card, #6773); the returned
     * function lets go. Idempotent.
     */
    hold(ids: readonly string[]): () => void {
      holds += 1;
      const key = holds;
      setHeld((m) => new Map(m).set(key, ids));
      return () =>
        setHeld((m) => {
          if (!m.has(key)) return m;
          const next = new Map(m);
          next.delete(key);
          return next;
        });
    },
  };
}

export type AcceptedMoves = ReturnType<typeof createAcceptedMoves>;
export type { MoveRead } from './move-read';

export const AcceptedMovesContext = createContext<AcceptedMoves | null>(null);

/** The page's accepted moves, or null outside a Fleet page (no note, tag, ring or Put back). */
export function useAcceptedMoves(): AcceptedMoves | null {
  return useContext(AcceptedMovesContext) ?? null;
}
